import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useI18n } from '../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_KEYS = ['cardio', 'adiposity', 'diabetes', 'senior', 'rehab', 'family', 'undecided'];

const SERVICE_VALUE_MAP = {
  cardio: 'Valoración Cardiometabólica',
  adiposity: 'Valoración de adiposidad y metabolismo',
  diabetes: 'Valoración integral de diabetes tipo 2',
  senior: 'Valoración Senior Saludable',
  rehab: 'Rehabilitación Funcional',
  family: 'Medicina de Familia',
  undecided: 'Aún no lo tengo claro',
};

const WaitlistForm = ({ defaultService = '', variant = 'light', testIdPrefix = 'waitlist' }) => {
  const { t, lang } = useI18n();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [service, setService] = useState(defaultService);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !service) return;
    if (!acceptedPrivacy) return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      await axios.post(`${API}/waitlist`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        service,
        language: lang,
        privacy_accepted: true,
      });
      setStatus('success');
      setName(''); setEmail(''); setPhone(''); setService(defaultService);
      setAcceptedPrivacy(false);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || t('form.error'));
    }
  };

  const isDark = variant === 'dark';
  const labelColor = isDark ? 'text-white/70' : 'text-cyan-vidat';
  const inputCls = isDark
    ? 'w-full bg-transparent border-0 border-b border-white/30 focus:border-white text-white py-3 px-1 outline-none placeholder:text-white/40 font-light'
    : 'vidat-input';
  const helperText = isDark ? 'text-white/75' : 'text-stone-600';
  const linkCls = isDark ? 'text-white underline' : 'text-cyan-vidat underline underline-offset-2 hover:text-[#17306a]';

  const canSubmit = name.trim() && email.trim() && service && acceptedPrivacy && status !== 'submitting';

  return (
    <form onSubmit={onSubmit} className="w-full" data-testid={`${testIdPrefix}-form`} noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
        <div>
          <label className={`block text-[11px] uppercase tracking-[0.22em] font-medium mb-2 ${labelColor}`}>{t('form.name')}</label>
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)} required
            className={inputCls} data-testid={`${testIdPrefix}-name`}
          />
        </div>
        <div>
          <label className={`block text-[11px] uppercase tracking-[0.22em] font-medium mb-2 ${labelColor}`}>{t('form.email')}</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className={inputCls} data-testid={`${testIdPrefix}-email`}
          />
        </div>
        <div>
          <label className={`block text-[11px] uppercase tracking-[0.22em] font-medium mb-2 ${labelColor}`}>{t('form.phone')}</label>
          <input
            type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
            className={inputCls} data-testid={`${testIdPrefix}-phone`}
          />
        </div>
        <div>
          <label className={`block text-[11px] uppercase tracking-[0.22em] font-medium mb-2 ${labelColor}`}>{t('form.service')}</label>
          <select
            value={service} onChange={(e) => setService(e.target.value)} required
            className={`${inputCls} appearance-none cursor-pointer`}
            data-testid={`${testIdPrefix}-service`}
            style={{ backgroundImage: 'none' }}
          >
            <option value="" disabled className="text-stone-500">{t('form.selectService')}</option>
            {SERVICE_KEYS.map((k) => (
              <option key={k} value={SERVICE_VALUE_MAP[k]} className="text-stone-800">{t(`form.services.${k}`)}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={`mt-10 text-xs ${helperText} font-light leading-relaxed border-l-2 ${isDark ? 'border-white/30' : 'border-[#0936F6]/40'} pl-5`} data-testid={`${testIdPrefix}-rgpd-clause`}>
        <strong className="font-medium">Responsable:</strong> VIDAT INSTITUTO DE INTELIGENCIA MEDICA, S.L.{' '}
        <strong className="font-medium">Finalidad:</strong> gestionar tu solicitud de información, contacto o incorporación a la lista de espera.{' '}
        <strong className="font-medium">Legitimación:</strong> consentimiento de la persona interesada.{' '}
        <strong className="font-medium">Destinatarios:</strong> no se cederán datos a terceros salvo obligación legal o proveedores necesarios para la gestión técnica del servicio.{' '}
        <strong className="font-medium">Derechos:</strong> puedes acceder, rectificar, suprimir y ejercer otros derechos escribiendo a{' '}
        <a href="mailto:info@vidat.cat" className={linkCls}>info@vidat.cat</a>. Información adicional en la{' '}
        <Link to="/politica-de-privacidad" className={linkCls}>Política de Privacidad</Link>.
      </div>

      <label className={`mt-6 flex items-start gap-3 cursor-pointer text-sm ${isDark ? 'text-white/85' : 'text-stone-700'} font-light leading-relaxed`}>
        <input
          type="checkbox"
          checked={acceptedPrivacy}
          onChange={(e) => setAcceptedPrivacy(e.target.checked)}
          className="mt-1.5 w-4 h-4 accent-[#1E3A8A] flex-shrink-0"
          data-testid={`${testIdPrefix}-accept-privacy`}
          required
        />
        <span>
          He leído y acepto la{' '}
          <Link to="/politica-de-privacidad" className={linkCls} data-testid={`${testIdPrefix}-privacy-link`}>
            Política de Privacidad
          </Link>.
        </span>
      </label>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5">
        <button
          type="submit"
          disabled={!canSubmit}
          className={isDark
            ? 'inline-flex items-center justify-center gap-2 bg-white text-[#1E3A8A] px-7 py-3.5 text-sm font-medium tracking-wide rounded-sm hover:bg-white/90 transition disabled:opacity-50 disabled:cursor-not-allowed'
            : 'btn-primary disabled:opacity-50 disabled:cursor-not-allowed'}
          data-testid={`${testIdPrefix}-submit`}
        >
          {status === 'submitting' ? t('form.submitting') : t('form.submit')}
        </button>

        {status === 'success' && (
          <div className={`text-sm ${isDark ? 'text-white' : 'text-cyan-vidat'}`} data-testid={`${testIdPrefix}-success`}>
            {t('form.success')}
          </div>
        )}
        {status === 'error' && (
          <div className="text-sm text-red-500" data-testid={`${testIdPrefix}-error`}>
            {errorMsg || t('form.error')}
          </div>
        )}
      </div>
    </form>
  );
};

export default WaitlistForm;
